
import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Upload, Link, X } from 'lucide-react';
import { toast } from 'sonner';

interface BlogImageUploadProps {
  value?: string;
  onChange: (url: string) => void;
}

const BlogImageUpload: React.FC<BlogImageUploadProps> = ({ value, onChange }) => {
  const [imageUrl, setImageUrl] = useState(value || '');
  const [uploading, setUploading] = useState(false);

  const handleUrlSubmit = () => {
    if (imageUrl) {
      onChange(imageUrl);
      toast.success('Image URL set successfully');
    }
  };

  const handleFileUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // For now, we'll use a placeholder URL since we don't have storage configured
    // In a real implementation, you would upload to Supabase Storage
    setUploading(true);
    
    // Simulate upload delay
    setTimeout(() => {
      const mockUrl = `https://images.unsplash.com/photo-1649972904349-6e44c42644a7?w=800`;
      onChange(mockUrl);
      setUploading(false);
      toast.success('Image uploaded successfully');
    }, 1000);
  };

  const handleRemoveImage = () => {
    onChange('');
    setImageUrl('');
    toast.success('Image removed');
  };

  return (
    <div className="space-y-4">
      <Label>Featured Image</Label>
      
      {value && (
        <div className="relative inline-block">
          <img 
            src={value} 
            alt="Featured" 
            className="w-full max-w-md h-48 object-cover rounded-lg border"
          />
          <Button
            type="button"
            variant="destructive"
            size="icon"
            className="absolute top-2 right-2"
            onClick={handleRemoveImage}
          >
            <X className="h-4 w-4" />
          </Button>
        </div>
      )}

      {!value && (
        <Tabs defaultValue="url" className="w-full">
          <TabsList className="grid w-full grid-cols-2">
            <TabsTrigger value="url">
              <Link className="w-4 h-4 mr-2" />
              Image URL
            </TabsTrigger>
            <TabsTrigger value="upload">
              <Upload className="w-4 h-4 mr-2" />
              Upload
            </TabsTrigger>
          </TabsList>
          
          <TabsContent value="url" className="space-y-4">
            <div className="flex gap-2">
              <Input
                placeholder="Enter image URL..."
                value={imageUrl}
                onChange={(e) => setImageUrl(e.target.value)}
                className="flex-1"
              />
              <Button
                type="button"
                onClick={handleUrlSubmit}
                disabled={!imageUrl}
              >
                Set Image
              </Button>
            </div>
          </TabsContent>
          
          <TabsContent value="upload" className="space-y-4">
            <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center">
              <input
                type="file"
                accept="image/*"
                onChange={handleFileUpload}
                className="hidden"
                id="image-upload"
                disabled={uploading}
              />
              <label
                htmlFor="image-upload"
                className="cursor-pointer flex flex-col items-center gap-2"
              >
                <Upload className="h-8 w-8 text-gray-400" />
                <span className="text-sm text-gray-600">
                  {uploading ? 'Uploading...' : 'Click to upload image'}
                </span>
              </label>
            </div>
          </TabsContent>
        </Tabs>
      )}
    </div>
  );
};

export default BlogImageUpload;
